import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Calendar, Plus } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";

// Mirrors the role gate on `/club`. Players see no button to avoid the
// frustrating "redirect to /dashboard" experience.
const CAN_CREATE_LEAGUE = new Set(["club_admin", "admin", "coach", "federation"]);

export default function Leagues() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState(false);
  useEffect(() => {
    api.get(`/leagues${mine ? "?mine=true" : ""}`).then((r) => setItems(r.data));
  }, [mine]);
  const canCreate = user && CAN_CREATE_LEAGUE.has(user.role);
  return (
    <AppLayout>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black">{t("leagues")}</h1>
          <p className="text-zinc-400 mt-2">Long-form competition. Track your rank.</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <div className="inline-flex rounded-xl bg-zinc-900 border border-zinc-800 p-1" data-testid="leagues-mine-toggle">
              <button data-testid="leagues-tab-all" onClick={() => setMine(false)} className={`px-3 py-2 rounded-lg text-sm ${!mine ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"}`}>All</button>
              <button data-testid="leagues-tab-mine" onClick={() => setMine(true)} className={`px-3 py-2 rounded-lg text-sm ${mine ? "bg-zinc-800 text-white" : "text-zinc-400 hover:text-white"}`}>My events</button>
            </div>
          )}
          {canCreate && (
            <Link to="/club" data-testid="leagues-create" className="pp-btn-primary px-5 py-3 rounded-xl flex items-center gap-2">
              <Plus size={18} /> {t("create_league")}
            </Link>
          )}
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="leagues-grid">
        {items.map((l) => (
          <Link key={l.id} to={`/leagues/${l.id}`} data-testid={`league-card-${l.id}`} className="pp-card p-6 block">
            <div className="text-xs text-[#00E676] uppercase tracking-wider flex items-center gap-1"><Trophy size={14} /> {l.sport?.name}</div>
            <div className="font-heading text-xl font-bold mt-2">{l.title}</div>
            <div className="text-sm text-zinc-400 mt-2 line-clamp-2">{l.description}</div>
            <div className="mt-4 flex justify-between items-center text-xs text-zinc-500">
              <span className="flex items-center gap-1"><Calendar size={12} /> {l.season_name}</span>
              <span className="text-[#00E676] font-semibold">{l.entry_fee > 0 ? `${l.entry_fee} ${l.currency}` : t("free")}</span>
            </div>
          </Link>
        ))}
        {items.length === 0 && <div className="pp-card p-10 text-zinc-500 col-span-full text-center" data-testid="leagues-empty">{mine ? "You haven't created any leagues yet." : t("no_results")}</div>}
      </div>
    </AppLayout>
  );
}
